import React, { useEffect, useState } from "react";
import { cn, timeAgo } from "@/lib/utils";
import { assetsApi, mediaUrl } from "@/lib/apiClient";
import { shouldFetchRenderedInstagramCards } from "@/lib/instagramCardPreview";
import { useAuth } from "@/state/AuthProvider";
import {
  ThumbsUp,
  MessageCircle,
  Send,
  Bookmark,
  Repeat2,
  Heart,
  Share2,
  MoreHorizontal,
  Globe,
  BadgeCheck,
  Play,
  Lightbulb,
  Smile,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  LinkedInLogo,
  XLogo,
  InstagramLogo,
  FacebookLogo,
  ThreadsLogo,
  BlueskyLogo,
  ShortsLogo,
} from "@/components/brand/PlatformLogos";

/* -------------------------------------------------------------------------- */
/*  Shared helpers                                                            */
/* -------------------------------------------------------------------------- */

function GradientAvatar({ initials = "AA", size = 44, ring }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
      style={{
        width: size,
        height: size,
        background:
          "linear-gradient(135deg, #2D6BFF 0%, #8FE04A 60%, #7DDFFF 100%)",
        boxShadow: ring || "inset 0 1px 0 rgba(255,255,255,0.12)",
      }}
      aria-hidden
    >
      {initials}
    </div>
  );
}

const FONT_SANS_NATIVE =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function hasPostText(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return Boolean(trimmed) && trimmed !== "-" && trimmed !== "\u2013" && trimmed !== "\u2014" && trimmed !== "â€”";
}

function joinPostText(parts) {
  return parts.filter(hasPostText).join("\n\n");
}

function RichPostText({ text, linkColor, className }) {
  const pieces = String(text || "").split(/(#[A-Za-z0-9_]+)/g);
  return (
    <p className={cn("whitespace-pre-wrap", className)}>
      {pieces.map((piece, index) => (
        piece.startsWith("#") ? (
          <span key={`${piece}-${index}`} style={{ color: linkColor }}>{piece}</span>
        ) : (
          <React.Fragment key={index}>{piece}</React.Fragment>
        )
      ))}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  LinkedIn — light feed look                                                */
/* -------------------------------------------------------------------------- */

export function LinkedInPreview({ asset, className }) {
  const { user } = useAuth();
  const body = asset.body || "";
  const isLong = body.length > 320;
  const truncated = isLong ? body.slice(0, 320).trimEnd() + "…" : body;
  return (
    <article
      data-testid="post-preview-linkedin"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#E0E0E0",
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        fontFamily: FONT_SANS_NATIVE,
        color: "#000000E6",
      }}
    >
      {/* Platform strip */}
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#EDEDED" }}>
        <div className="flex items-center gap-2">
          <LinkedInLogo size={18} />
          <span className="text-[11.5px] font-semibold tracking-tight" style={{ color: "#0A66C2" }}>LinkedIn</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#666" }}>Feed preview</span>
      </div>

      <div className="px-4 pt-3">
        <header className="flex items-start gap-2.5">
          <GradientAvatar initials={user?.initials || "AA"} size={48} />
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1 truncate text-[14px] font-semibold" style={{ color: "#000000E6" }}>
              {user?.name || "Account"}
              <BadgeCheck className="h-3.5 w-3.5" style={{ color: "#0A66C2" }} />
            </p>
            {(user?.headline || user?.role) && (
              <p className="line-clamp-1 text-[12px]" style={{ color: "#00000099" }}>
                {user?.headline || user?.role}
              </p>
            )}
            <p className="flex items-center gap-0.5 text-[11.5px]" style={{ color: "#00000080" }}>
              {timeAgo(asset.updated_at)} · <Globe className="h-3 w-3" />
            </p>
          </div>
          <MoreHorizontal className="h-5 w-5" style={{ color: "#00000080" }} />
        </header>

        <div className="mt-3 space-y-2.5 text-[14px] leading-[1.45]" style={{ color: "#000000E6" }}>
          {asset.hook && asset.hook !== "—" && (
            <p className="font-medium">{asset.hook}</p>
          )}
          <p className="whitespace-pre-wrap">
            {truncated}
            {isLong && (
              <span className="font-medium" style={{ color: "#00000080" }}> …see more</span>
            )}
          </p>
          {asset.cta && asset.cta !== "—" && (
            <p className="font-medium" style={{ color: "#000000E6" }}>{asset.cta}</p>
          )}
          {asset.hashtags?.length > 0 && (
            <p className="text-[14px]" style={{ color: "#0A66C2" }}>
              {asset.hashtags.join(" ")}
            </p>
          )}
        </div>
      </div>

      {/* Cover image (rendered like a LinkedIn attached image) */}
      {asset.cover_image && (
        <div className="mt-3 border-t border-b" style={{ borderColor: "#EDEDED" }}>
          <img src={asset.cover_image} alt="" className="block w-full" />
        </div>
      )}

      {/* Reactions count */}
      <div className="mt-3 flex items-center justify-between border-t px-4 py-1.5 text-[12px]" style={{ borderColor: "#EDEDED", color: "#00000099" }}>
        <div className="flex items-center gap-1">
          <span className="flex -space-x-1">
            <span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ backgroundColor: "#0A66C2" }}><ThumbsUp className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ backgroundColor: "#DF704D" }}><Heart className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ backgroundColor: "#F5BB5C" }}><Lightbulb className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
          </span>
          <span className="ml-1.5">428</span>
        </div>
        <div className="flex items-center gap-2">
          <span>37 comments</span>
          <span aria-hidden>·</span>
          <span>12 reposts</span>
        </div>
      </div>

      {/* Action bar */}
      <div className="grid grid-cols-4 border-t" style={{ borderColor: "#EDEDED" }}>
        <LiAction label="Like" icon={ThumbsUp} />
        <LiAction label="Comment" icon={MessageCircle} />
        <LiAction label="Repost" icon={Repeat2} />
        <LiAction label="Send" icon={Send} />
      </div>
    </article>
  );
}

function LiAction({ label, icon: Icon }) {
  return (
    <button type="button" className="flex items-center justify-center gap-1.5 py-2.5 text-[12.5px] font-semibold transition-colors hover:bg-black/[0.04]" style={{ color: "#00000099" }}>
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  X / Twitter — dark feed look                                              */
/* -------------------------------------------------------------------------- */

export function XPreview({ asset, className }) {
  const { user } = useAuth();
  const isThread = asset.asset_type === "thread";
  const posts = isThread
    ? [asset.hook, ...(asset.body || "").split(/\n\n+/).filter(Boolean)]
    : [
        [asset.hook, asset.body, asset.cta && asset.cta !== "—" ? asset.cta : null]
          .filter(Boolean)
          .join("\n\n"),
      ];
  return (
    <article
      data-testid="post-preview-x"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#000000",
        borderColor: "#2F3336",
        color: "#E7E9EA",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#2F3336" }}>
        <div className="flex items-center gap-2">
          <XLogo size={16} className="text-white" />
          <span className="text-[11.5px] font-semibold">For you</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#71767B" }}>X · post preview</span>
      </div>

      <div className="px-4 pt-3">
        <header className="flex items-start gap-3">
          <GradientAvatar initials={user?.initials || "AA"} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1 text-[14px]">
              <span className="font-bold" style={{ color: "#E7E9EA" }}>{user?.name || "Account"}</span>
              <BadgeCheck className="h-4 w-4" fill="#1D9BF0" stroke="#000" strokeWidth={1.5} />
              <span style={{ color: "#71767B" }}>@account</span>
              <span style={{ color: "#71767B" }}>·</span>
              <span style={{ color: "#71767B" }}>{timeAgo(asset.updated_at)}</span>
            </div>
            {isThread && (
              <p className="text-[12px]" style={{ color: "#71767B" }}>Thread · {posts.length} posts</p>
            )}
          </div>
          <MoreHorizontal className="h-4 w-4" style={{ color: "#71767B" }} />
        </header>

        <ol className="mt-2 space-y-2.5">
          {posts.slice(0, 6).map((p, i) => (
            <li key={i} className="relative pl-5">
              {isThread && posts.length > 1 && i < Math.min(posts.length, 6) - 1 && (
                <span aria-hidden className="absolute left-[6px] top-5 bottom-[-12px] w-px" style={{ backgroundColor: "#2F3336" }} />
              )}
              {isThread && (
                <span aria-hidden className="absolute left-0 top-1.5 h-3 w-3 rounded-full border-2" style={{ borderColor: "#1D9BF0", backgroundColor: "#000" }} />
              )}
              <p className="whitespace-pre-wrap text-[15px] leading-[1.4]" style={{ color: "#E7E9EA" }}>{p}</p>
            </li>
          ))}
          {posts.length > 6 && <li className="pl-5 text-[12.5px]" style={{ color: "#71767B" }}>Show {posts.length - 6} more replies</li>}
        </ol>

        {asset.hashtags?.length > 0 && (
          <p className="mt-2 text-[14px]" style={{ color: "#1D9BF0" }}>
            {asset.hashtags.join(" ")}
          </p>
        )}

        {/* Cover image attached to the lead post */}
        {asset.cover_image && (
          <div className="mt-3 overflow-hidden rounded-2xl border" style={{ borderColor: "#2F3336" }}>
            <img src={asset.cover_image} alt="" className="block w-full" />
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between border-t px-4 py-2 text-[12.5px]" style={{ borderColor: "#2F3336", color: "#71767B" }}>
        <XStat icon={MessageCircle} value="94" />
        <XStat icon={Repeat2} value="147" />
        <XStat icon={Heart} value="812" />
        <XStat icon={Bookmark} value="58" />
        <XStat icon={Share2} />
      </div>
    </article>
  );
}

function XStat({ icon: Icon, value }) {
  return (
    <button type="button" className="flex items-center gap-1 transition-colors hover:text-[color:#1D9BF0]">
      <Icon className="h-4 w-4" />
      {value && <span className="text-[12px]">{value}</span>}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Instagram — light feed look                                               */
/* -------------------------------------------------------------------------- */

export function InstagramPreview({ asset, className }) {
  const { user } = useAuth();
  // Derive carousel slides if body contains "Slide N · …" pattern
  const slides = (asset.body || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => /^slide\s*\d/i.test(l))
    .map((l) => l.replace(/^slide\s*\d+\s*[·:]\s*/i, ""));
  const isCarousel = slides.length > 1 || asset.asset_type === "carousel";
  const handle = (user?.name || "adam").toLowerCase().replace(/\s+/g, "");

  return (
    <article
      data-testid="post-preview-instagram"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#DBDBDB",
        color: "#262626",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-3 py-2" style={{ borderColor: "#EFEFEF" }}>
        <div className="flex items-center gap-2">
          <InstagramLogo size={18} />
          <span className="text-[11.5px] font-semibold">Instagram</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#737373" }}>Feed preview</span>
      </div>

      {/* Author row */}
      <header className="flex items-center gap-3 px-3 py-2.5">
        <div className="rounded-full p-[2px]" style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }}>
          <div className="rounded-full bg-white p-[2px]">
            <GradientAvatar initials={(user?.initials || "AA")} size={30} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-[13px] font-semibold">
            {handle}
            <BadgeCheck className="h-3.5 w-3.5" fill="#3897F0" stroke="#fff" strokeWidth={2} />
          </p>
          <p className="text-[11px]" style={{ color: "#737373" }}>Sponsored</p>
        </div>
        <MoreHorizontal className="h-4 w-4" style={{ color: "#262626" }} />
      </header>

      {/* Image / carousel mockup */}
      <div className="relative aspect-square w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #FAEEDB 0%, #F5C9E0 35%, #D0BAF5 70%, #BFD9FF 100%)" }} aria-hidden>
        {/* Decorative slide content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#7B5AA6" }}>
            {isCarousel ? `Slide 1 / ${Math.max(slides.length, 6)}` : "Carousel"}
          </p>
          <h3 className="mt-2 text-[24px] font-bold leading-[1.15] tracking-tight" style={{ color: "#1A1233" }}>
            {asset.hook || "Stop tuning prompts."}
          </h3>
          {slides[0] && (
            <p className="mt-2 line-clamp-2 text-[12.5px]" style={{ color: "#3D2860" }}>
              {slides[0]}
            </p>
          )}
        </div>

        {isCarousel && (
          <>
            <span className="absolute left-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white">
              <ChevronLeft className="h-4 w-4" />
            </span>
            <span className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white">
              <ChevronRight className="h-4 w-4" />
            </span>
            <span className="absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 text-[10.5px] font-semibold text-white">
              1/{Math.max(slides.length, 6)}
            </span>
          </>
        )}
      </div>

      {/* Carousel dots */}
      {isCarousel && (
        <div className="flex items-center justify-center gap-1 py-2">
          {Array.from({ length: Math.min(Math.max(slides.length, 6), 8) }).map((_, i) => (
            <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: i === 0 ? "#3897F0" : "#DBDBDB" }} />
          ))}
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center justify-between px-3 pt-2">
        <div className="flex items-center gap-3.5">
          <Heart className="h-6 w-6" />
          <MessageCircle className="h-6 w-6" />
          <Send className="h-6 w-6" />
        </div>
        <Bookmark className="h-6 w-6" />
      </div>

      <div className="px-3 pb-3 pt-2 text-[13px]">
        <p className="font-semibold">2,418 likes</p>
        <p className="mt-1">
          <span className="font-semibold">{handle}</span>{" "}
          <span className="whitespace-pre-wrap">{asset.hook}</span>
        </p>
        {asset.cta && asset.cta !== "—" && (
          <p className="mt-1" style={{ color: "#262626" }}>{asset.cta}</p>
        )}
        {asset.hashtags?.length > 0 && (
          <p className="mt-1" style={{ color: "#003569" }}>{asset.hashtags.join(" ")}</p>
        )}
        <p className="mt-2 text-[12px] uppercase tracking-[0.04em]" style={{ color: "#737373" }}>
          {timeAgo(asset.updated_at)}
        </p>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Facebook — light feed look                                                */
/* -------------------------------------------------------------------------- */

export function FacebookPreview({ asset, className }) {
  const { user } = useAuth();
  return (
    <article
      data-testid="post-preview-facebook"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#DDDFE2",
        color: "#050505",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#EBEDF0", backgroundColor: "#F0F2F5" }}>
        <div className="flex items-center gap-2">
          <FacebookLogo size={18} />
          <span className="text-[11.5px] font-semibold" style={{ color: "#1877F2" }}>Facebook</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#65676B" }}>News Feed preview</span>
      </div>

      <div className="px-4 pt-3">
        <header className="flex items-start gap-2.5">
          <GradientAvatar initials={user?.initials || "AA"} size={42} />
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold" style={{ color: "#050505" }}>{user?.name || "Account"}</p>
            <p className="flex items-center gap-1 text-[12.5px]" style={{ color: "#65676B" }}>
              {timeAgo(asset.updated_at)} · <Globe className="h-3 w-3" />
            </p>
          </div>
          <MoreHorizontal className="h-5 w-5" style={{ color: "#65676B" }} />
        </header>

        <div className="mt-3 space-y-2 text-[15px] leading-[1.4]" style={{ color: "#050505" }}>
          {asset.hook && asset.hook !== "—" && <p className="font-semibold">{asset.hook}</p>}
          <p className="whitespace-pre-wrap">{asset.body}</p>
          {asset.cta && asset.cta !== "—" && (
            <p className="text-[14.5px]" style={{ color: "#1877F2" }}>{asset.cta}</p>
          )}
        </div>
      </div>

      {asset.cover_image && (
        <img src={asset.cover_image} alt="" className="mt-3 block w-full" />
      )}

      <div className="mt-3 flex items-center justify-between border-t px-4 py-1.5 text-[13px]" style={{ borderColor: "#EBEDF0", color: "#65676B" }}>
        <div className="flex items-center gap-1.5">
          <span className="flex -space-x-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#1877F2" }}><ThumbsUp className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#F33E58" }}><Heart className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#F7B125" }}><Smile className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
          </span>
          <span className="ml-1.5">1.4K</span>
        </div>
        <div className="flex items-center gap-3">
          <span>96 comments</span>
          <span>42 shares</span>
        </div>
      </div>

      <div className="grid grid-cols-3 border-t" style={{ borderColor: "#EBEDF0" }}>
        <FbAction label="Like" icon={ThumbsUp} />
        <FbAction label="Comment" icon={MessageCircle} />
        <FbAction label="Share" icon={Share2} />
      </div>
    </article>
  );
}

function FbAction({ label, icon: Icon }) {
  return (
    <button type="button" className="flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold transition-colors hover:bg-black/[0.04]" style={{ color: "#65676B" }}>
      <Icon className="h-[18px] w-[18px]" />
      {label}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Threads — dark feed look                                                  */
/* -------------------------------------------------------------------------- */

export function ThreadsPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = (user?.name || "adam").toLowerCase().replace(/\s+/g, "");
  const body = [asset.hook, asset.body, asset.cta && asset.cta !== "—" ? asset.cta : null].filter(Boolean).join("\n\n");
  return (
    <article
      data-testid="post-preview-threads"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#0A0A0A",
        borderColor: "#262626",
        color: "#F3F5F7",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#1F1F1F" }}>
        <div className="flex items-center gap-2">
          <ThreadsLogo size={16} className="text-white" />
          <span className="text-[11.5px] font-semibold">Threads</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#999" }}>For you</span>
      </div>

      <div className="px-4 pt-4">
        <header className="flex items-start gap-3">
          <GradientAvatar initials={user?.initials || "AA"} size={36} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 text-[14px]">
              <span className="font-semibold">{handle}</span>
              <BadgeCheck className="h-3.5 w-3.5" fill="#F3F5F7" stroke="#0A0A0A" strokeWidth={2} />
              <span style={{ color: "#777" }} className="ml-1 text-[12.5px]">{timeAgo(asset.updated_at)}</span>
            </div>
            <p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-[1.45]">{body}</p>
            {asset.hashtags?.length > 0 && (
              <p className="mt-1.5 text-[14px]" style={{ color: "#A8C5FF" }}>{asset.hashtags.join(" ")}</p>
            )}
            {asset.cover_image && (
              <div className="mt-3 overflow-hidden rounded-xl border" style={{ borderColor: "#262626" }}>
                <img src={asset.cover_image} alt="" className="block w-full" />
              </div>
            )}
          </div>
          <MoreHorizontal className="h-4 w-4" style={{ color: "#777" }} />
        </header>

        <div className="ml-12 mt-3 flex items-center gap-5 pb-3 text-[13px]" style={{ color: "#E1E1E1" }}>
          <button className="inline-flex items-center gap-1.5 hover:opacity-80"><Heart className="h-[18px] w-[18px]" /> 1.2K</button>
          <button className="inline-flex items-center gap-1.5 hover:opacity-80"><MessageCircle className="h-[18px] w-[18px]" /> 84</button>
          <button className="inline-flex items-center gap-1.5 hover:opacity-80"><Repeat2 className="h-[18px] w-[18px]" /> 36</button>
          <button className="inline-flex items-center gap-1.5 hover:opacity-80"><Send className="h-[18px] w-[18px]" /> 24</button>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Bluesky — light feed look                                                 */
/* -------------------------------------------------------------------------- */

export function BlueskyPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = (user?.name || "adam").toLowerCase().replace(/\s+/g, "") + ".bsky.social";
  const body = [asset.hook, asset.body, asset.cta && asset.cta !== "—" ? asset.cta : null].filter(Boolean).join("\n\n");
  return (
    <article
      data-testid="post-preview-bluesky"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#D6E1EB",
        color: "#0B1620",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#E6EEF6", backgroundColor: "#F5F9FD" }}>
        <div className="flex items-center gap-2">
          <BlueskyLogo size={18} />
          <span className="text-[11.5px] font-semibold" style={{ color: "#1185FE" }}>Bluesky</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#6B7886" }}>Following</span>
      </div>

      <div className="px-4 pt-3">
        <header className="flex items-start gap-3">
          <GradientAvatar initials={user?.initials || "AA"} size={42} />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold">{user?.name || "Account"}</p>
            <p className="text-[12.5px]" style={{ color: "#6B7886" }}>@{handle} · {timeAgo(asset.updated_at)}</p>
          </div>
          <MoreHorizontal className="h-4 w-4" style={{ color: "#6B7886" }} />
        </header>

        <p className="mt-2.5 whitespace-pre-wrap text-[15px] leading-[1.5]" style={{ color: "#0B1620" }}>{body}</p>
        {asset.hashtags?.length > 0 && (
          <p className="mt-2 text-[14.5px]" style={{ color: "#1185FE" }}>{asset.hashtags.join(" ")}</p>
        )}
        {asset.cover_image && (
          <div className="mt-3 overflow-hidden rounded-xl border" style={{ borderColor: "#D6E1EB" }}>
            <img src={asset.cover_image} alt="" className="block w-full" />
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-6 border-t px-4 py-2 text-[12.5px]" style={{ borderColor: "#E6EEF6", color: "#6B7886" }}>
        <button className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><MessageCircle className="h-4 w-4" /> 12</button>
        <button className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Repeat2 className="h-4 w-4" /> 38</button>
        <button className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Heart className="h-4 w-4" /> 184</button>
        <button className="ml-auto inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Share2 className="h-4 w-4" /></button>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  YouTube Shorts — vertical phone mockup                                    */
/* -------------------------------------------------------------------------- */

export function ShortsPreview({ asset, className }) {
  const { user } = useAuth();
  const lines = (asset.body || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <article
      data-testid="post-preview-shorts"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{
        backgroundColor: "#0F0F0F",
        borderColor: "#272727",
        color: "#F1F1F1",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "#272727" }}>
        <div className="flex items-center gap-2">
          <ShortsLogo size={18} />
          <span className="text-[11.5px] font-semibold">Shorts</span>
        </div>
        <span className="text-[10.5px]" style={{ color: "#AAA" }}>45-second vertical script</span>
      </div>

      <div className="flex gap-4 p-4">
        {/* Vertical 9:16 phone mockup */}
        <div
          className="relative shrink-0 overflow-hidden rounded-2xl border"
          style={{
            width: 172,
            height: 306,
            borderColor: "#272727",
            background: "linear-gradient(160deg, #1a0a16 0%, #421131 45%, #FF0033 110%)",
          }}
          aria-hidden
        >
          <div className="absolute inset-0 opacity-70" style={{
            backgroundImage:
              "radial-gradient(160px 160px at 30% 22%, rgba(255,90,106,0.45), transparent 60%), radial-gradient(180px 180px at 70% 78%, rgba(143, 224, 74,0.22), transparent 60%)",
          }} />
          {/* Hook text */}
          <div className="absolute inset-x-3 top-4 text-[13px] font-bold leading-[1.15] tracking-tight text-white drop-shadow-md">
            {asset.hook}
          </div>
          {/* Big play button */}
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 backdrop-blur">
            <Play className="h-6 w-6 fill-white text-white" />
          </span>
          {/* Profile + handle (pushed up to avoid right-rail overlap) */}
          <div className="absolute left-3 right-14 bottom-[58px] flex items-center gap-2">
            <GradientAvatar initials={user?.initials || "AA"} size={22} />
            <span className="truncate text-[11px] font-semibold text-white">@{(user?.name || "adam").toLowerCase().replace(/\s+/g, "")}</span>
            <span className="ml-auto shrink-0 rounded-md border border-white/55 px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-white">Subscribe</span>
          </div>
          {/* Caption strip */}
          <div className="absolute inset-x-3 bottom-[30px] text-[10px] font-medium text-white/85 line-clamp-1">
            {asset.cta && asset.cta !== "—" ? asset.cta : "Tap to watch the full 45s."}
          </div>
          {/* Progress + time */}
          <div className="absolute inset-x-3 bottom-2.5">
            <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5 text-[9.5px] text-white">0:08 / 0:45</div>
            <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/20"><div className="h-full w-[18%]" style={{ backgroundColor: "#FF0033" }} /></div>
          </div>
          {/* Right rail icons */}
          <div className="absolute right-2 bottom-12 flex flex-col items-center gap-2.5 text-white/95">
            <span className="flex flex-col items-center"><Heart className="h-4 w-4" /><span className="text-[9.5px] font-semibold leading-none mt-0.5">3.2K</span></span>
            <span className="flex flex-col items-center"><MessageCircle className="h-4 w-4" /><span className="text-[9.5px] font-semibold leading-none mt-0.5">128</span></span>
            <span className="flex flex-col items-center"><Send className="h-4 w-4" /><span className="text-[9.5px] font-semibold leading-none mt-0.5">Share</span></span>
          </div>
        </div>

        {/* Script */}
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "#AAA" }}>Script · 45s</p>
          <ol className="mt-2 space-y-1.5 font-mono text-[11.5px] leading-[1.6]" style={{ color: "#E5E5E5" }}>
            {lines.slice(0, 6).map((l, i) => (
              <li key={i} className="truncate">{l}</li>
            ))}
          </ol>
          {asset.cta && asset.cta !== "—" && (
            <p className="mt-3 text-[11.5px]" style={{ color: "#AAA" }}>
              CTA · <span style={{ color: "#FF6B7E" }}>{asset.cta}</span>
            </p>
          )}
          {asset.hashtags?.length > 0 && (
            <p className="mt-2 text-[12px]" style={{ color: "#7AB3FF" }}>{asset.hashtags.join(" ")}</p>
          )}
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Image prompt — unchanged styling                                          */
/* -------------------------------------------------------------------------- */

export function ImagePromptPreview({ asset, className }) {
  const [copied, setCopied] = useState(false);
  const promptText = (asset.body || "").trim();

  const onCopy = async () => {
    if (!promptText) return;
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      toast.success("Image prompt copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  return (
    <article
      data-testid="post-preview-image"
      className={cn("rounded-xl border bg-card p-5 inner-highlight", className)}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]" style={{ borderColor: "rgba(246,178,74,0.30)", color: "#FFD9A3", backgroundColor: "rgba(246,178,74,0.06)" }}>
          Image prompt
        </span>
        <button
          type="button"
          onClick={onCopy}
          disabled={!promptText}
          data-testid="image-prompt-copy"
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] font-medium text-foreground/85 transition-colors hover:text-1 disabled:opacity-50"
          style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
      </header>
      <p className="mb-2 text-[11.5px] text-muted-foreground">
        Paste this into ChatGPT, Gemini, or any image tool to create a matching visual.
      </p>
      <div
        className="rounded-lg border p-3.5"
        style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
      >
        <p className="whitespace-pre-wrap text-[13px] leading-[1.65] text-foreground/90" data-testid="image-prompt-text">
          {promptText || "No prompt was generated for this run."}
        </p>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  High-fidelity feed previews for primary channels                           */
/* -------------------------------------------------------------------------- */

function LinkedInPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const authorName = user?.name || "Account";
  const headline = user?.headline || user?.role;
  const postText = joinPostText([asset.hook, asset.body, asset.cta]);
  const isLong = postText.length > 720;
  const displayText = isLong && !expanded ? postText.slice(0, 720).trimEnd() : postText;
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];

  return (
    <article
      data-testid="post-preview-linkedin"
      className={cn("overflow-hidden rounded-lg border", className)}
      style={{
        backgroundColor: "#FFFFFF",
        borderColor: "#D6D9DC",
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        fontFamily: FONT_SANS_NATIVE,
        color: "#000000E6",
      }}
    >
      <div className="px-4 pt-3">
        <header className="flex items-start gap-3">
          <GradientAvatar initials={user?.initials || "AA"} size={48} ring="0 0 0 1px rgba(0,0,0,0.08)" />
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1 truncate text-[14px] font-semibold leading-[1.2]" style={{ color: "#000000E6" }}>
              {authorName}
              <BadgeCheck className="h-3.5 w-3.5 shrink-0" fill="#0A66C2" stroke="#FFFFFF" strokeWidth={2.2} />
            </p>
            {headline && (
              <p className="line-clamp-1 text-[12px] leading-[1.25]" style={{ color: "#00000099" }}>
                {headline}
              </p>
            )}
            <p className="flex items-center gap-1 text-[11.5px] leading-[1.3]" style={{ color: "#00000099" }}>
              <span>{timeAgo(asset.updated_at)}</span>
              <span aria-hidden>-</span>
              <Globe className="h-3 w-3" />
            </p>
          </div>
          <button type="button" className="rounded-full p-1.5 transition-colors hover:bg-black/[0.06]" aria-label="More">
            <MoreHorizontal className="h-5 w-5" style={{ color: "#00000099" }} />
          </button>
        </header>

        <div className="mt-3 text-[14px] leading-[1.45]" style={{ color: "#000000E6" }}>
          <RichPostText text={displayText} linkColor="#0A66C2" />
          {isLong && !expanded && (
            <button
              type="button"
              aria-label="See more"
              onClick={() => setExpanded(true)}
              className="mt-1 text-[14px] font-semibold hover:underline"
              style={{ color: "#00000099" }}
            >
              ...see more
            </button>
          )}
          {hashtags.length > 0 && (
            <p className="mt-2 text-[14px] font-medium" style={{ color: "#0A66C2" }}>
              {hashtags.join(" ")}
            </p>
          )}
        </div>
      </div>

      {asset.cover_image && (
        <div className="mt-3 border-y" style={{ borderColor: "#E0E0E0" }}>
          <img src={asset.cover_image} alt="" className="block max-h-[520px] w-full object-cover" />
        </div>
      )}

      <div className={cn("flex items-center justify-between px-4 py-2 text-[12px]", !asset.cover_image && "mt-2")} style={{ color: "#00000099" }}>
        <div className="flex items-center gap-1">
          <span className="flex -space-x-1">
            <span className="flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-white" style={{ backgroundColor: "#0A66C2" }}><ThumbsUp className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-white" style={{ backgroundColor: "#DF704D" }}><Heart className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-white" style={{ backgroundColor: "#F5BB5C" }}><Lightbulb className="h-2.5 w-2.5" fill="#fff" stroke="#fff" /></span>
          </span>
          <span className="ml-1.5">428</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span>37 comments</span>
          <span aria-hidden>-</span>
          <span>12 reposts</span>
        </div>
      </div>

      <div className="mx-4 grid grid-cols-4 border-t" style={{ borderColor: "#E0E0E0" }}>
        <LiAction label="Like" icon={ThumbsUp} />
        <LiAction label="Comment" icon={MessageCircle} />
        <LiAction label="Repost" icon={Repeat2} />
        <LiAction label="Send" icon={Send} />
      </div>
    </article>
  );
}

function XPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const isThread = asset.asset_type === "thread";
  const leadText = joinPostText([
    asset.hook,
    isThread ? null : asset.body,
    !isThread ? asset.cta : null,
  ]);
  const threadPosts = isThread
    ? [asset.hook, ...(asset.body || "").split(/\n\n+/), asset.cta].filter(hasPostText)
    : [leadText].filter(hasPostText);
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];

  return (
    <article
      data-testid="post-preview-x"
      className={cn("overflow-hidden rounded-2xl border", className)}
      style={{
        backgroundColor: "#000000",
        borderColor: "#2F3336",
        color: "#E7E9EA",
        fontFamily: FONT_SANS_NATIVE,
      }}
    >
      {threadPosts.slice(0, 5).map((post, index) => (
        <XFeedPost
          key={`${index}-${post.slice(0, 24)}`}
          asset={asset}
          user={user}
          text={post}
          index={index}
          total={threadPosts.length}
          showMedia={index === 0}
          hashtags={index === threadPosts.length - 1 ? hashtags : []}
        />
      ))}
      {threadPosts.length > 5 && (
        <button type="button" className="block w-full border-t px-4 py-3 text-left text-[14px]" style={{ borderColor: "#2F3336", color: "#1D9BF0" }}>
          Show {threadPosts.length - 5} more posts
        </button>
      )}
    </article>
  );
}

function XFeedPost({ asset, user, text, index, total, showMedia, hashtags }) {
  const isThread = total > 1;
  const authorName = user?.name || "Account";
  const handle = (user?.handle || "account").replace(/^@/, "");

  return (
    <section className={cn("relative flex gap-3 px-4 py-3", index > 0 && "border-t")} style={{ borderColor: "#2F3336" }}>
      <div className="relative shrink-0">
        <GradientAvatar initials={user?.initials || "AA"} size={40} ring="0 0 0 1px rgba(255,255,255,0.12)" />
        {isThread && index < total - 1 && (
          <span aria-hidden className="absolute left-1/2 top-11 h-[calc(100%+18px)] w-0.5 -translate-x-1/2" style={{ backgroundColor: "#2F3336" }} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <header className="flex min-w-0 items-start gap-2">
          <div className="min-w-0 flex flex-wrap items-center gap-1 text-[15px] leading-[1.25]">
            <span className="truncate font-bold" style={{ color: "#E7E9EA" }}>{authorName}</span>
            <BadgeCheck className="h-4 w-4 shrink-0" fill="#1D9BF0" stroke="#000000" strokeWidth={1.5} />
            <span className="truncate" style={{ color: "#71767B" }}>@{handle}</span>
            <span style={{ color: "#71767B" }}>-</span>
            <span style={{ color: "#71767B" }}>{timeAgo(asset.updated_at)}</span>
          </div>
          <button type="button" className="ml-auto rounded-full p-1 transition-colors hover:bg-white/10" aria-label="More">
            <MoreHorizontal className="h-4 w-4" style={{ color: "#71767B" }} />
          </button>
        </header>

        <RichPostText text={text} linkColor="#1D9BF0" className="mt-1 text-[15px] leading-[1.45]" />

        {hashtags.length > 0 && (
          <p className="mt-2 text-[14px]" style={{ color: "#1D9BF0" }}>{hashtags.join(" ")}</p>
        )}

        {showMedia && asset.cover_image && (
          <div className="mt-3 overflow-hidden rounded-2xl border" style={{ borderColor: "#2F3336" }}>
            <img src={asset.cover_image} alt="" className="block max-h-[480px] w-full object-cover" />
          </div>
        )}

        <div className="mt-3 flex max-w-[520px] items-center justify-between text-[13px]" style={{ color: "#71767B" }}>
          <XPreviewAction icon={MessageCircle} value={index === 0 ? "94" : "12"} />
          <XPreviewAction icon={Repeat2} value={index === 0 ? "147" : "31"} />
          <XPreviewAction icon={Heart} value={index === 0 ? "812" : "186"} />
          <XPreviewAction icon={BarChart3} value={index === 0 ? "18K" : "4K"} />
          <XPreviewAction icon={Bookmark} />
          <XPreviewAction icon={Share2} />
        </div>
      </div>
    </section>
  );
}

function XPreviewAction({ icon: Icon, value }) {
  return (
    <button type="button" className="flex items-center gap-1 transition-colors hover:text-[color:#1D9BF0]">
      <Icon className="h-4 w-4" />
      {value && <span className="text-[12px]">{value}</span>}
    </button>
  );
}

function getSocialHandle(user, fallback = "account") {
  return (user?.handle || user?.name || fallback)
    .replace(/^@/, "")
    .toLowerCase()
    .replace(/[^a-z0-9_\.]+/g, "");
}

// Text-only fallback slides (used only when the real rendered cards can't be
// fetched, e.g. offline). Derived from the post's OWN content \u2014 no placeholder
// marketing copy.
function getCarouselSlides(asset) {
  const parsed = (asset.body || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => /^slide\s*\d/i.test(line))
    .map((line) => line.replace(/^slide\s*\d+\s*[-:.\u00b7]\s*/i, ""));
  if (parsed.length) return parsed;
  return [asset.hook, asset.body, asset.cta].filter(hasPostText);
}

// Fetch the REAL rendered Instagram card image(s) for this asset so the preview
// matches what will actually post. Falls back to null (the text mock) offline.
function useAssetCards(asset) {
  const [urls, setUrls] = useState(null);
  const id = asset?.id;
  const platform = asset?.platform;
  const { hook, body, cta } = asset || {};
  // Re-render when the content or chosen style changes (extracted so the deps
  // array stays statically checkable).
  const styleKey = JSON.stringify(asset?.card_style || null);
  const shouldFetchCards = shouldFetchRenderedInstagramCards(asset);
  useEffect(() => {
    let alive = true;
    if (!shouldFetchCards) {
      setUrls(null);
      return undefined;
    }
    assetsApi
      .cards(id)
      .then((r) => { if (alive) setUrls(Array.isArray(r?.urls) ? r.urls.map(mediaUrl) : []); })
      .catch(() => { if (alive) setUrls(null); });
    return () => { alive = false; };
  }, [id, platform, hook, body, cta, styleKey, shouldFetchCards]);
  return urls;
}

function SocialImage({ asset, children, aspect = "aspect-square", className }) {
  if (asset.cover_image) {
    return <img src={asset.cover_image} alt="" className={cn("block w-full object-cover", aspect, className)} />;
  }
  return (
    <div className={cn("relative w-full overflow-hidden", aspect, className)} style={{ background: "linear-gradient(135deg, #111827 0%, #0F766E 48%, #8FE04A 100%)" }}>
      <div aria-hidden className="absolute inset-0 opacity-70" style={{ backgroundImage: "radial-gradient(360px 240px at 24% 20%, rgba(255,255,255,0.28), transparent 62%), radial-gradient(340px 240px at 88% 84%, rgba(15,23,42,0.45), transparent 64%)" }} />
      {children}
    </div>
  );
}

function InstagramPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = getSocialHandle(user);
  const realCards = useAssetCards(asset);
  const [slideIdx, setSlideIdx] = useState(0);
  useEffect(() => { setSlideIdx(0); }, [realCards]);

  const textSlides = getCarouselSlides(asset);
  const total = realCards && realCards.length ? realCards.length : textSlides.length;
  const isCarousel = total > 1 || asset.asset_type === "carousel";
  const caption = joinPostText([asset.hook, asset.body, asset.cta]);
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];
  const go = (dir) => setSlideIdx((i) => Math.min(Math.max(i + dir, 0), Math.max(total - 1, 0)));

  return (
    <article
      data-testid="post-preview-instagram"
      className={cn("overflow-hidden rounded-lg border", className)}
      style={{ backgroundColor: "#FFFFFF", borderColor: "#DBDBDB", color: "#262626", fontFamily: FONT_SANS_NATIVE }}
    >
      <header className="flex items-center gap-3 px-3 py-2.5">
        <div className="rounded-full p-[2px]" style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }}>
          <div className="rounded-full bg-white p-[2px]">
            <GradientAvatar initials={user?.initials || "AA"} size={32} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 truncate text-[13px] font-semibold">
            {handle}
            <BadgeCheck className="h-3.5 w-3.5 shrink-0" fill="#3897F0" stroke="#fff" strokeWidth={2} />
          </p>
          {user?.organization && (
            <p className="truncate text-[11px]" style={{ color: "#737373" }}>{user.organization}</p>
          )}
        </div>
        <button type="button" aria-label="More" className="rounded-full p-1">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </header>

      <div className="relative aspect-square overflow-hidden border-y" style={{ borderColor: "#EFEFEF", background: "linear-gradient(135deg, #102A43 0%, #0F766E 48%, #155E75 100%)" }}>
        {realCards && realCards.length ? (
          // The actual rendered card(s) — exactly what will publish.
          <img src={realCards[Math.min(slideIdx, realCards.length - 1)]} alt={`Slide ${slideIdx + 1}`} className="absolute inset-0 h-full w-full object-cover" data-testid="instagram-preview-card" />
        ) : (
          // Offline fallback: a simple text mock from the post's own content.
          <>
            <div aria-hidden className="absolute inset-0 opacity-80" style={{ backgroundImage: "radial-gradient(320px 220px at 20% 14%, rgba(255,255,255,0.22), transparent 62%), radial-gradient(360px 260px at 82% 88%, rgba(143, 224, 74,0.34), transparent 64%)" }} />
            <div className="absolute inset-0 flex flex-col items-start justify-center px-9 text-left text-white">
              <h3 className="max-w-[88%] text-[30px] font-black leading-[1.04] tracking-tight drop-shadow-sm">{textSlides[slideIdx] || textSlides[0]}</h3>
            </div>
          </>
        )}
        {isCarousel && (
          <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="instagram-preview-counter">
            {Math.min(slideIdx + 1, total)}/{total}
          </span>
        )}
        {isCarousel && slideIdx > 0 && (
          <button type="button" onClick={() => go(-1)} aria-label="Previous slide" data-testid="instagram-preview-prev" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-1 text-slate-900 shadow hover:bg-white">
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {isCarousel && slideIdx < total - 1 && (
          <button type="button" onClick={() => go(1)} aria-label="Next slide" data-testid="instagram-preview-next" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-1 text-slate-900 shadow hover:bg-white">
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>

      {isCarousel && (
        <div className="flex items-center justify-center gap-1 py-2">
          {Array.from({ length: Math.min(total, 10) }).map((_, index) => (
            <button key={index} type="button" onClick={() => setSlideIdx(index)} aria-label={`Go to slide ${index + 1}`} className="h-1.5 w-1.5 rounded-full transition-colors" style={{ backgroundColor: index === slideIdx ? "#3897F0" : "#DBDBDB" }} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between px-3 pt-2">
        <div className="flex items-center gap-3.5">
          <Heart className="h-6 w-6" />
          <MessageCircle className="h-6 w-6" />
          <Send className="h-6 w-6" />
        </div>
        <Bookmark className="h-6 w-6" />
      </div>

      <div className="px-3 pb-3 pt-2 text-[13px] leading-[1.35]">
        <p className="font-semibold">2,418 likes</p>
        <p className="mt-1">
          <span className="font-semibold">{handle}</span>{" "}
          <span className="whitespace-pre-wrap">{caption}</span>
        </p>
        {hashtags.length > 0 && <p className="mt-1" style={{ color: "#003569" }}>{hashtags.join(" ")}</p>}
        <p className="mt-1" style={{ color: "#737373" }}>View all 96 comments</p>
        <p className="mt-2 text-[11px] uppercase tracking-[0.04em]" style={{ color: "#737373" }}>{timeAgo(asset.updated_at)}</p>
      </div>
    </article>
  );
}

function FacebookPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const body = joinPostText([asset.hook, asset.body, asset.cta]);
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];

  return (
    <article
      data-testid="post-preview-facebook"
      className={cn("overflow-hidden rounded-lg border", className)}
      style={{ backgroundColor: "#FFFFFF", borderColor: "#DADDE1", color: "#050505", fontFamily: FONT_SANS_NATIVE, boxShadow: "0 1px 2px rgba(0,0,0,0.10)" }}
    >
      <div className="px-4 pt-3">
        <header className="flex items-start gap-2.5">
          <GradientAvatar initials={user?.initials || "AA"} size={42} ring="0 0 0 1px rgba(0,0,0,0.08)" />
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold leading-[1.2]">{user?.name || "Account"}</p>
            <p className="flex items-center gap-1 text-[12.5px]" style={{ color: "#65676B" }}>
              {timeAgo(asset.updated_at)}
              <span aria-hidden>-</span>
              <Globe className="h-3 w-3" />
            </p>
          </div>
          <button type="button" aria-label="More" className="rounded-full p-1.5 transition-colors hover:bg-black/[0.06]">
            <MoreHorizontal className="h-5 w-5" style={{ color: "#65676B" }} />
          </button>
        </header>
      </div>

      <SocialImage asset={asset} aspect="aspect-video" className="mt-3 border-y" />

      <div className="px-4 pt-3">
        <RichPostText text={body} linkColor="#1877F2" className="text-[15px] leading-[1.42]" />
        {hashtags.length > 0 && <p className="mt-2 text-[14.5px]" style={{ color: "#1877F2" }}>{hashtags.join(" ")}</p>}
      </div>

      <div className="flex items-center justify-between px-4 py-2 text-[13px]" style={{ color: "#65676B" }}>
        <div className="flex items-center gap-1.5">
          <span className="flex -space-x-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#1877F2" }}><ThumbsUp className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#F33E58" }}><Heart className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white" style={{ backgroundColor: "#F7B125" }}><Smile className="h-3 w-3" fill="#fff" stroke="#fff" /></span>
          </span>
          <span className="ml-1">1.4K</span>
        </div>
        <div className="flex items-center gap-3">
          <span>96 comments</span>
          <span>42 shares</span>
        </div>
      </div>

      <div className="mx-4 grid grid-cols-3 border-t" style={{ borderColor: "#CED0D4" }}>
        <FbAction label="Like" icon={ThumbsUp} />
        <FbAction label="Comment" icon={MessageCircle} />
        <FbAction label="Share" icon={Share2} />
      </div>
    </article>
  );
}

function ThreadsPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = getSocialHandle(user);
  const body = joinPostText([asset.hook, asset.body, asset.cta]);
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];

  return (
    <article
      data-testid="post-preview-threads"
      className={cn("overflow-hidden rounded-2xl border", className)}
      style={{ backgroundColor: "#0A0A0A", borderColor: "#262626", color: "#F3F5F7", fontFamily: FONT_SANS_NATIVE }}
    >
      <section className="flex gap-3 px-4 py-4">
        <div className="relative shrink-0">
          <GradientAvatar initials={user?.initials || "AA"} size={38} />
          <span aria-hidden className="absolute left-1/2 top-11 h-[calc(100%-8px)] w-0.5 -translate-x-1/2" style={{ backgroundColor: "#2F2F2F" }} />
        </div>
        <div className="min-w-0 flex-1">
          <header className="flex items-start gap-2">
            <div className="min-w-0 flex flex-wrap items-center gap-1 text-[14px]">
              <span className="font-semibold">{handle}</span>
              <BadgeCheck className="h-3.5 w-3.5" fill="#F3F5F7" stroke="#0A0A0A" strokeWidth={2} />
              <span className="ml-1 text-[12.5px]" style={{ color: "#777" }}>{timeAgo(asset.updated_at)}</span>
            </div>
            <button type="button" aria-label="More" className="ml-auto rounded-full p-1">
              <MoreHorizontal className="h-4 w-4" style={{ color: "#777" }} />
            </button>
          </header>

          <RichPostText text={body} linkColor="#A8C5FF" className="mt-1.5 text-[15px] leading-[1.45]" />
          {hashtags.length > 0 && <p className="mt-1.5 text-[14px]" style={{ color: "#A8C5FF" }}>{hashtags.join(" ")}</p>}

          {asset.cover_image && (
            <div className="mt-3 overflow-hidden rounded-xl border" style={{ borderColor: "#262626" }}>
              <img src={asset.cover_image} alt="" className="block max-h-[440px] w-full object-cover" />
            </div>
          )}

          <div className="mt-3 flex items-center gap-5 text-[13px]" style={{ color: "#E1E1E1" }}>
            <button type="button" className="inline-flex items-center gap-1.5 hover:opacity-80"><Heart className="h-[18px] w-[18px]" /> 1.2K</button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:opacity-80"><MessageCircle className="h-[18px] w-[18px]" /> 84</button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:opacity-80"><Repeat2 className="h-[18px] w-[18px]" /> 36</button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:opacity-80"><Send className="h-[18px] w-[18px]" /></button>
          </div>
        </div>
      </section>

      <div className="flex gap-3 px-4 pb-4">
        <GradientAvatar initials="?" size={30} ring="0 0 0 1px rgba(255,255,255,0.10)" />
        <div className="flex min-w-0 flex-1 items-center rounded-full border px-3 text-[13px]" style={{ borderColor: "#262626", color: "#777" }}>
          Reply to {handle}
        </div>
      </div>
    </article>
  );
}

function BlueskyPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = `${getSocialHandle(user)}.bsky.social`;
  const body = joinPostText([asset.hook, asset.body, asset.cta]);
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags : [];

  return (
    <article
      data-testid="post-preview-bluesky"
      className={cn("overflow-hidden rounded-xl border", className)}
      style={{ backgroundColor: "#FFFFFF", borderColor: "#D6E1EB", color: "#0B1620", fontFamily: FONT_SANS_NATIVE }}
    >
      <section className="flex gap-3 px-4 py-3">
        <GradientAvatar initials={user?.initials || "AA"} size={42} ring="0 0 0 1px rgba(17,133,254,0.18)" />
        <div className="min-w-0 flex-1">
          <header className="flex items-start gap-2">
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold">{user?.name || "Account"}</p>
              <p className="truncate text-[12.5px]" style={{ color: "#6B7886" }}>@{handle} - {timeAgo(asset.updated_at)}</p>
            </div>
            <button type="button" aria-label="More" className="ml-auto rounded-full p-1">
              <MoreHorizontal className="h-4 w-4" style={{ color: "#6B7886" }} />
            </button>
          </header>

          <RichPostText text={body} linkColor="#1185FE" className="mt-2 text-[15px] leading-[1.5]" />
          {hashtags.length > 0 && <p className="mt-2 text-[14.5px]" style={{ color: "#1185FE" }}>{hashtags.join(" ")}</p>}
          {asset.cover_image && (
            <div className="mt-3 overflow-hidden rounded-xl border" style={{ borderColor: "#D6E1EB" }}>
              <img src={asset.cover_image} alt="" className="block max-h-[430px] w-full object-cover" />
            </div>
          )}

          <div className="mt-3 flex items-center gap-7 text-[12.5px]" style={{ color: "#6B7886" }}>
            <button type="button" className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><MessageCircle className="h-4 w-4" /> 12</button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Repeat2 className="h-4 w-4" /> 38</button>
            <button type="button" className="inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Heart className="h-4 w-4" /> 184</button>
            <button type="button" className="ml-auto inline-flex items-center gap-1.5 hover:text-[color:#1185FE]"><Share2 className="h-4 w-4" /></button>
          </div>
        </div>
      </section>
    </article>
  );
}

function ShortsPlatformPreview({ asset, className }) {
  const { user } = useAuth();
  const handle = getSocialHandle(user);
  const caption = hasPostText(asset.cta) ? asset.cta : asset.hook;

  return (
    <article
      data-testid="post-preview-shorts"
      className={cn("mx-auto w-full max-w-[360px] overflow-hidden rounded-[28px] border bg-black", className)}
      style={{ borderColor: "#272727", color: "#F1F1F1", fontFamily: FONT_SANS_NATIVE, boxShadow: "0 28px 70px rgba(0,0,0,0.24)" }}
    >
      <div className="relative aspect-[9/16] overflow-hidden bg-black">
        <SocialImage asset={asset} aspect="absolute inset-0 h-full w-full">
          <div className="absolute inset-0 flex items-start px-5 pt-16 text-white">
            <h3 className="max-w-[72%] text-[27px] font-black leading-[1.06] tracking-tight drop-shadow-md">{asset.hook}</h3>
          </div>
        </SocialImage>
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />

        <div className="absolute left-4 right-16 top-4 flex items-center justify-between text-white">
          <span className="text-[15px] font-bold">Shorts</span>
          <span className="rounded-full bg-black/40 px-2 py-0.5 text-[11px]">0:08</span>
        </div>

        <div className="absolute bottom-5 left-4 right-16 text-white">
          <div className="flex items-center gap-2">
            <GradientAvatar initials={user?.initials || "AA"} size={28} />
            <span className="truncate text-[13px] font-semibold">@{handle}</span>
            <button type="button" className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-black">Subscribe</button>
          </div>
          <p className="mt-3 line-clamp-2 text-[13px] font-medium leading-[1.35]">{caption}</p>
          {asset.hashtags?.length > 0 && <p className="mt-1 line-clamp-1 text-[12px] text-white/85">{asset.hashtags.join(" ")}</p>}
        </div>

        <div className="absolute bottom-20 right-3 flex flex-col items-center gap-5 text-white">
          <button type="button" className="flex flex-col items-center gap-1"><Heart className="h-7 w-7" /><span className="text-[11px] font-semibold">3.2K</span></button>
          <button type="button" className="flex flex-col items-center gap-1"><MessageCircle className="h-7 w-7" /><span className="text-[11px] font-semibold">128</span></button>
          <button type="button" className="flex flex-col items-center gap-1"><Share2 className="h-7 w-7" /><span className="text-[11px] font-semibold">Share</span></button>
          <button type="button" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15"><MoreHorizontal className="h-5 w-5" /></button>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dispatcher                                                                */
/* -------------------------------------------------------------------------- */

export function PostPreview({ asset, className }) {
  if (!asset) return null;
  if (asset.asset_type === "image_prompt") return <ImagePromptPreview asset={asset} className={className} />;
  if (asset.asset_type === "video_script" || asset.platform === "youtube_shorts") return <ShortsPlatformPreview asset={asset} className={className} />;
  switch (asset.platform) {
    case "x":
    case "twitter":
      return <XPlatformPreview asset={asset} className={className} />;
    case "instagram":
      return <InstagramPlatformPreview asset={asset} className={className} />;
    case "facebook":
      return <FacebookPlatformPreview asset={asset} className={className} />;
    case "threads":
      return <ThreadsPlatformPreview asset={asset} className={className} />;
    case "bluesky":
      return <BlueskyPlatformPreview asset={asset} className={className} />;
    case "linkedin":
    default:
      return <LinkedInPlatformPreview asset={asset} className={className} />;
  }
}
